import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';

function InvoicePrint() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [invoice, setInvoice] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        loadInvoice();
    }, [id]);

    const loadInvoice = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/invoices/${id}`);
            if (response.success) {
                setInvoice(response.data);
                setTimeout(() => {
                    window.print();
                }, 500);
            } else {
                setError(response.message || 'Failed to load invoice');
            }
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white print:hidden">
                <div className="w-12 h-12 border-4 border-slate-200 border-t-indigo-600 rounded-full animate-spin mb-4"></div>
            </div>
        );
    }

    if (error) return <div className="mt-16 p-8 text-center text-rose-600 font-medium print:hidden">{error}</div>;
    if (!invoice) return <div className="mt-16 p-8 text-center text-slate-500 font-medium print:hidden">Invoice not found.</div>;

    const firm = (window as any).CURRENT_FIRM || {};

    return (
        <div className="bg-slate-100 min-h-screen font-sans text-slate-900 print:bg-white pt-8 print:pt-0">
            {/* Control Panel - Hidden when printing */}
            <div className="max-w-[210mm] mx-auto bg-white border border-slate-200 p-4 rounded-xl shadow-sm print:hidden mb-8 sticky top-4 z-50 flex items-center justify-between">
                <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="px-6 py-2 bg-slate-100 text-slate-600 font-bold text-sm rounded-lg hover:bg-slate-200 transition-colors"
                >
                    &larr; Return
                </button>
                <button
                    type="button"
                    onClick={() => window.print()}
                    className="px-6 py-2 bg-indigo-600 text-white font-bold text-sm rounded-lg hover:bg-indigo-700 transition-colors shadow-md"
                >
                    Print Document
                </button>
            </div>

            {/* A4 Printable Document Container */}
            <div className="bg-white shadow-2xl print:shadow-none min-h-[297mm] w-[210mm] mx-auto p-[10mm] sm:p-[15mm] flex flex-col relative overflow-hidden">
                <style>
                    {`
                        @media print {
                            @page { size: A4 portrait; margin: 0; }
                            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                            .print-avoid-break { break-inside: avoid; }
                        }
                    `}
                </style>

                {/* Print Header */}
                <header className="flex justify-between items-start border-b-2 border-slate-900 pb-8 mb-8">
                    <div>
                        <h1 className="text-4xl font-black uppercase tracking-tighter text-slate-900 mb-2">TAX INVOICE</h1>
                        <p className="text-sm font-bold text-slate-500 uppercase tracking-widest">INV-NO-00{invoice.invoice_number}</p>
                    </div>
                    <div className="text-right">
                        <h2 className="text-xl font-black text-slate-900">{firm.name || 'COMPANY NAME'}</h2>
                        <p className="text-sm font-medium text-slate-500 mt-1">{firm.address || 'Company Address Line'}</p>
                        <p className="text-sm font-medium text-slate-500">{firm.city ? `${firm.city}, ` : ''}{firm.state || ''} {firm.zip_code || ''}</p>
                        {firm.phone && <p className="text-sm font-medium text-slate-500 mt-2">Tel: {firm.phone}</p>}
                        {firm.email && <p className="text-sm font-medium text-slate-400">{firm.email}</p>}
                    </div>
                </header>

                <div className="grid grid-cols-2 gap-12 mb-10">
                    <div>
                        <h3 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-3">Billed To</h3>
                        <p className="text-lg font-black text-slate-900 uppercase">{invoice.customer_name || 'Retail Client'}</p>
                    </div>
                    <div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1">Issue Date</h3>
                                <p className="text-sm font-bold text-slate-900">{new Date(invoice.invoice_date || invoice.created_at).toLocaleDateString()}</p>
                            </div>
                            <div>
                                <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1">Due Date</h3>
                                <p className="text-sm font-bold text-slate-900">{invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : 'Upon Receipt'}</p>
                            </div>
                            <div className="col-span-2 mt-2 pt-2 border-t border-slate-50">
                                <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1">Payment Method</h3>
                                <p className="text-sm font-bold text-slate-900 uppercase tracking-tight">
                                    {invoice.payment_mode?.replace('_', ' ') || 'CASH'}
                                    {invoice.transaction_id && <span className="ml-2 text-slate-400 text-xs font-mono font-normal">/ TXN: {invoice.transaction_id}</span>}
                                    {invoice.cheque_number && <span className="ml-2 text-slate-400 text-xs font-mono font-normal">/ CHQ: {invoice.cheque_number}</span>}
                                </p>
                            </div>
                            {invoice.order_id && (
                                <div className="col-span-2 mt-2 pt-2 border-t border-slate-50">
                                    <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1">Source Reference</h3>
                                    <p className="text-sm font-bold text-slate-900">Order #{invoice.order_number}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex-1">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="border-b-2 border-slate-200">
                                <th className="py-3 font-black text-[10px] text-slate-500 uppercase tracking-widest w-[50%]">Item Description</th>
                                <th className="py-3 text-center font-black text-[10px] text-slate-500 uppercase tracking-widest">Qty</th>
                                <th className="py-3 text-right font-black text-[10px] text-slate-500 uppercase tracking-widest">MRP</th>
                                <th className="py-3 text-right font-black text-[10px] text-slate-500 uppercase tracking-widest">Price</th>
                                <th className="py-3 text-right font-black text-[10px] text-slate-500 uppercase tracking-widest">Total</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {invoice.items?.map((item: any, idx: number) => (
                                <tr key={item.id || idx} className="print-avoid-break">
                                    <td className="py-4 pr-4">
                                        <p className="font-bold text-slate-900 uppercase text-sm">{item.product_name}</p>
                                        {item.product_unit_identifier && (
                                            <p className="text-[10px] text-slate-500 font-mono mt-1 w-fit bg-slate-50 px-2 py-0.5 rounded">
                                                SN: {item.product_unit_identifier}
                                            </p>
                                        )}
                                    </td>
                                    <td className="py-4 text-center font-mono font-bold">{item.quantity}</td>
                                    <td className="py-4 text-right font-mono text-slate-400 text-xs">₹{item.mrp?.toLocaleString()}</td>
                                    <td className="py-4 text-right font-mono font-bold whitespace-nowrap">₹{item.unit_price?.toLocaleString()}</td>
                                    <td className="py-4 text-right font-mono font-black text-slate-900 whitespace-nowrap">₹{item.total?.toLocaleString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="mt-8 border-t-2 border-slate-900 pt-6 flex justify-between print-avoid-break">
                    <div className="w-[50%]">
                        {invoice.notes && (
                            <div className="mb-6">
                                <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2">Remarks</h3>
                                <p className="text-xs text-slate-600 leading-relaxed font-medium">{invoice.notes}</p>
                            </div>
                        )}
                        {invoice.terms && (
                            <div>
                                <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2">Terms & Conditions</h3>
                                <p className="text-[10px] text-slate-500 leading-relaxed font-bold italic">{invoice.terms}</p>
                            </div>
                        )}
                    </div>

                    <div className="w-[40%] bg-slate-50 p-6 rounded-2xl">
                        <div className="space-y-3 font-mono text-sm">
                            <div className="flex justify-between text-slate-600 font-bold">
                                <span>Subtotal</span>
                                <span>₹{invoice.subtotal?.toLocaleString()}</span>
                            </div>
                            {(invoice.total_igst || 0) > 0 ? (
                                <div className="flex justify-between text-slate-600 font-bold">
                                    <span>IGST</span>
                                    <span>₹{invoice.total_igst?.toLocaleString()}</span>
                                </div>
                            ) : (
                                <>
                                    <div className="flex justify-between text-slate-600 font-bold">
                                        <span>CGST</span>
                                        <span>₹{(invoice.total_cgst || (invoice.tax_amount || 0) / 2).toLocaleString()}</span>
                                    </div>
                                    <div className="flex justify-between text-slate-600 font-bold">
                                        <span>SGST</span>
                                        <span>₹{(invoice.total_sgst || (invoice.tax_amount || 0) / 2).toLocaleString()}</span>
                                    </div>
                                </>
                            )}

                            {invoice.discount_amount > 0 && (
                                <div className="flex justify-between text-slate-500">
                                    <span>Discount (-).</span>
                                    <span>₹{invoice.discount_amount?.toLocaleString()}</span>
                                </div>
                            )}
                            <div className="pt-4 mt-2 border-t border-slate-200 flex justify-between text-xl font-black text-slate-900">
                                <span>Grand Total</span>
                                <span>₹{invoice.total_amount?.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between text-emerald-600/80 font-bold text-xs pt-2">
                                <span>Amount Paid</span>
                                <span>₹{invoice.paid_amount?.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between text-rose-600/80 font-bold text-xs">
                                <span>Balance Due</span>
                                <span>₹{invoice.balance_due?.toLocaleString()}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default InvoicePrint;
